import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, NgModel } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { filter, take } from 'rxjs/operators';
import {
  MyErrorStateMatcher,
  ValidatorConfig,
} from '../../../../../../../common/field-validation/my-error-state-matcher';
import { UiValidator } from '../../../../../../../common/field-validation/ui-validator.dto';
import { FieldConfigDTO, LookupChange, LookupEntityConf } from '../../../../../../../system/system-dtos';
import { isDefined } from '../../../../../../utils/utils';
import { DataTypeComponent } from '../../../../data-type-component';
import { LookupService } from '../../lookup.service';
import {
  LookupPagedModalComponent,
  LookupPagedModalInitialData,
} from '../lookup-paged-modal/lookup-paged-modal.component';

/**
 * Form field / table cell for FieldType.paged_lookup.
 *
 * Unlike the legacy `lookup` type, this never resolves its selected entity from a locally
 * cached collection (BlastV2Service) - every resolution of the currently selected id, and
 * every search performed from the picker modal, is a dedicated server call
 * (see LookupService.searchPaged). This is intended for lookup collections that are too
 * large to reasonably keep in memory on the client (e.g. free spins).
 */
@Component({
  selector: 'app-lookup-paged-form-field',
  templateUrl: './lookup-paged-form-field.component.html',
  styleUrls: ['./lookup-paged-form-field.component.scss'],
  standalone: false,
})
export class LookupPagedFormFieldComponent extends DataTypeComponent implements OnInit {
  @Input() data: any;
  @Input() editMode: boolean;
  @Input() fieldConfig: FieldConfigDTO;
  @Input() formGroup: UntypedFormGroup;

  @Input() lookup: string;
  @Input() fieldName: any;

  rowData: any;

  label = '';
  loading = false;

  lookupCode = '';
  editReadOnly = false;

  private lookupConfig: LookupEntityConf;

  matcher = new MyErrorStateMatcher();
  private validatorsSet = false;

  constructor(
    private lookupService: LookupService,
    private dialog: MatDialog,
    private ref: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.lookupCode = this.getLookupcode();
    this.lookupConfig = this.lookupService.getConfByLookupCode(this.lookupCode);

    if (this.formGroup) {
      const control = this.formGroup.get(this.getFieldName());
      this.editReadOnly = control ? control.disabled : false;

      if (!this.data || typeof this.data === 'string') {
        const id = this.data ? this.data : control?.value;
        if (!id) {
          this.initialise();
          return;
        }
        this.resolveById(id);
        return;
      }
    }

    this.label = this.extractLabel(this.data);
    this.initialise();
  }

  private resolveById(id: string) {
    this.loading = true;
    this.lookupService
      .searchPaged(this.getUrl(), 0, 1, id, this.formGroup ? this.formGroup.getRawValue() : {})
      .pipe(take(1))
      .subscribe({
        next: (resp) => {
          this.data = resp && resp.content && resp.content.length ? resp.content[0] : undefined;
          this.label = this.extractLabel(this.data);
          this.loading = false;
          this.initialise();
          this.ref.markForCheck();
        },
        error: (err) => {
          console.error('Failed to resolve paged lookup value', id, err);
          this.loading = false;
          this.initialise();
          this.ref.markForCheck();
        },
      });
  }

  private extractLabel(data: any): string {
    if (!isDefined(data)) {
      return '';
    }
    if (typeof data !== 'object') {
      return String(data);
    }

    const configured = this.runConfiguredExtractLabel(data);
    if (this.isUsableLabel(configured)) {
      return configured;
    }

    return this.buildFallbackLabel(data);
  }

  private runConfiguredExtractLabel(data: any): string {
    if (!isDefined(this.lookupConfig) || !isDefined(this.lookupConfig.extractLabel)) {
      return null;
    }
    try {
      const func = new Function('self', 'data', this.lookupConfig.extractLabel);
      return func(this, data);
    } catch (err) {
      console.error('Failed to extract label', this.lookupConfig.extractLabel, data, err);
      return null;
    }
  }

  private isUsableLabel(label: any): boolean {
    return isDefined(label) && String(label).trim() !== '' && String(label).trim() !== 'undefined';
  }

  private buildFallbackLabel(data: any): string {
    const idField = this.fieldConfig && this.fieldConfig.idFieldName ? this.fieldConfig.idFieldName : 'id';
    const id = isDefined(data[idField]) ? data[idField] : data.id;
    const name = data.name || data.code || data.title;

    if (isDefined(id) && name) {
      return `${name} (${id})`;
    }
    if (isDefined(id)) {
      return String(id);
    }
    return name || '';
  }

  private getUrl(): string {
    return (
      (this.fieldConfig && this.fieldConfig.selectUrl) || (this.lookupConfig && this.lookupConfig.defaultSelectUrl)
    );
  }

  private getLookupcode() {
    return this.lookup ? this.lookup : this.fieldConfig.lookup;
  }

  private getFieldName() {
    return this.fieldName ? this.fieldName : this.fieldConfig.name;
  }

  openModal() {
    const matDialogRef = this.dialog.open(LookupPagedModalComponent, {
      minWidth: '800px',
      data: {
        lookup: this.getLookupcode(),
        fieldConfig: this.fieldConfig,
        context: this.formGroup ? this.formGroup.getRawValue() : {},
      } as LookupPagedModalInitialData,
    });

    matDialogRef
      .afterClosed()
      .pipe(filter((ret) => !!ret))
      .subscribe((ret) => {
        const fieldName = this.getFieldName();

        let val = ret;
        if (isDefined(this.lookupConfig) && isDefined(this.lookupConfig.retValue)) {
          try {
            const func = new Function('self', 'data', this.lookupConfig.retValue);
            val = func(this, ret);
          } catch (err) {
            console.error('Error extracting return value, using row object', err, ret, this.lookupConfig);
          }
        }

        this.data = ret;
        this.label = this.extractLabel(ret);
        this.formGroup.get(fieldName).setValue(val);

        this.lookupService.getLookupChangeSubject().next({
          field: fieldName,
          value: this.formGroup.get(fieldName).value,
        } as LookupChange);

        this.ref.markForCheck();
      });
  }

  public clearField() {
    this.label = null;
    this.data = null;
    this.formGroup.get(this.getFieldName()).setValue(null);
  }

  condition(v: UiValidator, ngModel: NgModel) {
    if (!this.validatorsSet) {
      this.validatorsSet = true;
      ngModel.control.setValidators(MyErrorStateMatcher.convertToAngularValidators([this.fieldConfig]));
    }

    return this.getValidatorConfig(v).condition(ngModel.control);
  }

  message(v: UiValidator) {
    return this.getValidatorConfig(v).message(v);
  }

  private getValidatorConfig(v: UiValidator): ValidatorConfig {
    return MyErrorStateMatcher.messageMap[v.type];
  }

  setRowData(entity: any) {
    this.rowData = entity;
  }
}
